import React, { Fragment, useEffect, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { ArrowPathIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { classNames } from 'utils/styleUtils';
import {
  fetchGroups,
  fetchGroup,
  deleteGroup,
  syncGroup,
  selectGroups,
  selectScimLoading,
  selectDeleteConfirmGroupId,
  selectSyncingGroupIds,
  selectCurrentGroup,
  setDeleteConfirmGroupId,
  clearCurrentGroup,
  type IScimGroupList,
} from '../reducers/scimManagementSlice';
import ScimGroupDetail from './ScimGroupDetail';

const ScimGroupList = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const groups = useAppSelector(selectGroups);
  const loading = useAppSelector(selectScimLoading);
  const deleteConfirmId = useAppSelector(selectDeleteConfirmGroupId);
  const currentGroup = useAppSelector(selectCurrentGroup);
  const syncingGroupIds = useAppSelector(selectSyncingGroupIds);

  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  // Group added via picker but not yet saved — shown in the table even with mapping_count === 0
  const [pendingGroupId, setPendingGroupId] = useState<number | null>(null);

  useEffect(() => {
    void dispatch(fetchGroups());
  }, [dispatch]);

  const mappedGroups = groups.filter((g) => g.mapping_count > 0);
  const unmappedGroups = groups.filter((g) => g.mapping_count === 0);

  // Once the pending group is saved and gets mapping_count > 0, clear the pending state
  const pendingGroup = pendingGroupId !== null ? groups.find((g) => g.id === pendingGroupId) ?? null : null;
  const isPendingAlreadyMapped = pendingGroup !== null && pendingGroup.mapping_count > 0;

  // The full list to render: mapped groups + the pending unmapped group (if any, and not already in mapped)
  const displayGroups =
    pendingGroup !== null && !isPendingAlreadyMapped ? [...mappedGroups, pendingGroup] : mappedGroups;

  const filteredUnmapped =
    pickerQuery.trim() === ''
      ? unmappedGroups
      : unmappedGroups.filter(
          (g) =>
            g.display_name.toLowerCase().includes(pickerQuery.toLowerCase()) ||
            g.scim_external_id.toLowerCase().includes(pickerQuery.toLowerCase()),
        );

  const handleEdit = (groupId: number): void => {
    if (currentGroup?.id === groupId) {
      dispatch(clearCurrentGroup());
      if (groupId === pendingGroupId) {
        setPendingGroupId(null);
      }
    } else {
      void dispatch(fetchGroup(groupId));
    }
  };

  const handleSync = (groupId: number): void => {
    void dispatch(syncGroup(groupId));
  };

  const handleDeleteRequest = (groupId: number): void => {
    dispatch(setDeleteConfirmGroupId(groupId));
  };

  const handleDeleteConfirm = (): void => {
    if (deleteConfirmId !== null) {
      void dispatch(deleteGroup(deleteConfirmId));
    }
  };

  const handlePickerChange = (group: IScimGroupList | null): void => {
    if (group !== null) {
      setPendingGroupId(group.id);
      void dispatch(fetchGroup(group.id));
      setShowGroupPicker(false);
      setPickerQuery('');
    }
  };

  const handlePickerClose = (): void => {
    setShowGroupPicker(false);
    setPickerQuery('');
  };

  return (
    <div className="rounded-md shadow-sm bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h2 className="text-dataops-dark text-base">SCIM Groups</h2>
        <button
          onClick={() => setShowGroupPicker((v) => !v)}
          className="flex items-center gap-1 bg-dataops-primary-blue hover:bg-hover-primary-blue text-white rounded px-3 py-1.5 text-sm"
        >
          <PlusIcon className="h-4 w-4" />
          New Group Mapping
        </button>
      </div>

      {showGroupPicker && (
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
          <Combobox value={null} onChange={handlePickerChange}>
            <div className="relative flex-1 min-w-0">
              <Combobox.Button
                as="div"
                className="relative cursor-default overflow-hidden rounded border border-gray-300 bg-white text-sm flex items-center"
              >
                <Combobox.Input
                  className="w-full py-1.5 pl-3 pr-8 text-sm text-primary-text focus:outline-none"
                  displayValue={(g: IScimGroupList | null) => g?.display_name ?? ''}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  placeholder="Search for a SCIM group…"
                  aria-label="Search SCIM groups"
                  autoFocus
                />
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                </span>
              </Combobox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {filteredUnmapped.length === 0 ? (
                    <div className="relative cursor-default select-none py-2 px-4 text-secondary-text">
                      {unmappedGroups.length === 0
                        ? 'All groups already have mappings.'
                        : 'No groups match your search.'}
                    </div>
                  ) : (
                    filteredUnmapped.map((g) => (
                      <Combobox.Option
                        key={g.id}
                        value={g}
                        className={({ active }) =>
                          classNames(
                            active ? 'bg-gray-100 text-primary-text' : 'text-primary-text',
                            'relative cursor-default select-none py-2 pl-3 pr-9',
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={classNames(selected ? '' : 'font-normal', 'block truncate')}>
                              {g.display_name}
                              <span className="text-secondary-text ml-2 font-mono text-xs">{g.scim_external_id}</span>
                            </span>
                            {selected && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-dataops-primary-blue">
                                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                              </span>
                            )}
                          </>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </Transition>
            </div>
          </Combobox>
          <button onClick={handlePickerClose} className="text-secondary-text text-sm hover:text-primary-text">
            Cancel
          </button>
        </div>
      )}

      <table className="w-full divide-y divide-gray-300 table-fixed text-sm">
        <thead>
          <tr className="bg-dataops-dark divide-x divide-white">
            <th className="w-5/12 py-3 pl-4 pr-3 text-left text-white">Group Name</th>
            <th className="w-3/12 px-3 py-3 text-left text-white">External ID</th>
            <th className="w-2/12 px-3 py-3 text-left text-white">Members</th>
            <th className="w-2/12 px-3 py-3 text-white" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {!loading &&
            displayGroups.map((group) => {
              const isSyncing = syncingGroupIds.includes(group.id);
              return (
                <React.Fragment key={group.id}>
                  <tr
                    className={`even:bg-gray-50 ${currentGroup?.id === group.id ? 'bg-blue-50 even:bg-blue-50' : ''}`}
                  >
                    <td className="py-3 pl-4 pr-3 text-primary-text truncate">{group.display_name}</td>
                    <td className="px-3 py-3 text-secondary-text font-mono truncate">{group.scim_external_id}</td>
                    <td className="px-3 py-3 text-primary-text">{group.member_count}</td>
                    <td className="px-3 py-3">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleSync(group.id)}
                          aria-label="Sync group"
                          disabled={isSyncing}
                          title="Sync GitLab group memberships"
                          className={`flex items-center gap-1 rounded px-3 py-1.5 text-sm bg-dataops-primary-blue hover:bg-hover-primary-blue text-white ${
                            isSyncing ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <ArrowPathIcon className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                          Sync
                        </button>
                        <button
                          onClick={() => handleEdit(group.id)}
                          aria-label="Edit group"
                          className={`flex items-center gap-1 rounded px-3 py-1.5 text-sm text-white ${
                            currentGroup?.id === group.id
                              ? 'bg-dataops-secondary-blue opacity-75'
                              : 'bg-dataops-secondary-blue hover:bg-hover-secondary-blue'
                          }`}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteRequest(group.id)}
                          aria-label="Delete group"
                          className="flex items-center gap-1 rounded px-3 py-1.5 text-sm bg-red-highlight hover:bg-red-700 text-white"
                        >
                          <TrashIcon className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {currentGroup?.id === group.id && (
                    <tr>
                      <td colSpan={4} className="p-0 border-t-2 border-dataops-primary-blue">
                        <ScimGroupDetail />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          {loading && (
            <tr className="animate-[pulse_1.5s_cubic-bezier(0.4,_0,_0.6,_0.1)_infinite]">
              {[...Array(4)].map((_, i) => (
                <td key={i} className="px-3 py-3">
                  <div className="h-3 bg-slate-300 rounded" />
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>

      {!loading && displayGroups.length === 0 && (
        <div className="m-4 text-sm text-gray-600">
          No SCIM groups with mappings. Use &apos;New Group Mapping&apos; to configure one.
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirmId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-md shadow-lg p-6 max-w-sm w-full space-y-4">
            <p className="text-primary-text text-sm">
              Are you sure you want to delete this group? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => dispatch(setDeleteConfirmGroupId(null))}
                className="text-secondary-text text-sm hover:text-primary-text"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="bg-red-highlight hover:bg-red-700 text-white rounded px-4 py-1.5 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScimGroupList;
